import React from 'react'
import { Controller, useForm } from 'react-hook-form'
import { makeStyles } from '@material-ui/core'
import PropTypes from 'prop-types'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import SydButton from '../../../commonDesign/Button'
import SydModalActions from '../../../commonDesign/SydModal/SydModalActions'
import SydLabel from '../../../commonDesign/SydLabel'
import KdpInput from '../../../pages/admin/shared/KdpControls/KdpInput'
import DateTimePicker from '../../../atoms/DateTimePicker'

dayjs.extend(utc)

const useStyles = makeStyles((theme) => ({
  kdpLabel: {
    display: 'flex',
    flexDirection: 'row',
    gap: '35px',
    alignItems: 'center',
    paddingBottom: '20px'
  },
  name: {
    fontSize: theme.typography.fontSizes.xl,
    fontWeight: theme.typography.weights.light
  },
  code: {
    fontSize: theme.typography.fontSizes.md,
    fontWeight: theme.typography.weights.light,
    color: theme.palette.text.secondary
  }
}))

const isDateFormat = (format) => {
  return format?.startsWith('@') || format?.startsWith('&')
}

const normalizeDateValue = (value, format) => {
  if (!value) return ''
  if (format?.startsWith('@')) {
    const d = dayjs.utc(value)
    return d.isValid() ? d.format('YYYY-MM-DD') : ''
  }
  const d = dayjs(value)
  return d.isValid() ? d.format('YYYY-MM-DD') : ''
}

const DateInput = ({ name, control, format, defaultValue }) => {
  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      render={(f) => {
        const { ref: _ref, onChange, ...field } = f.field
        const dateFormat = format?.substring(1) || 'YYYY-MM-DD'
        return (
          <DateTimePicker
            {...field}
            value={field.value || defaultValue}
            onChange={(d) => onChange(d && dayjs(d).isValid() ? dayjs(d).format(dateFormat) : '')}
            format={dateFormat}
            inputProps={{ style: { fontSize: 18 } }}
          />
        )
      }}
    />
  )
}

DateInput.propTypes = {
  name: PropTypes.string.isRequired,
  control: PropTypes.object.isRequired,
  format: PropTypes.string,
  defaultValue: PropTypes.string
}

const getElementByFormat = (kdp, format, control) => {
  if (isDateFormat(format)) {
    const defaultValue = normalizeDateValue(kdp.value, format)
    return <DateInput name={kdp.codeName} control={control} format={format} defaultValue={defaultValue} />
  }
  return <KdpInput name={kdp.codeName} control={control} datapoint={kdp} />
}

const KDPDialogForm = ({ kdp, format, close, onSave, onError, isSaving }) => {
  const classes = useStyles()
  const form = useForm({
    mode: 'onChange',
    shouldUnregister: true,
    defaultValues: {
      [kdp?.codeName]: kdp?.value || ''
    }
  })
  return (
    <div>
      <div className={classes.kdpLabel}>
        <div className='flex flex-col gap-1'>
          <SydLabel label={kdp.displayName} className={classes.name} />
          <SydLabel label={kdp.codeName} variant='secondary' className={classes.code} />
        </div>
        <div className='flex flex-col gap-2'>
          {getElementByFormat(kdp, format, form.control)}
        </div>
      </div>
      <SydModalActions className='__actions'>
        <SydButton onClick={close} disabled={isSaving}>Cancel</SydButton>
        <SydButton variant='primary' onClick={form.handleSubmit(onSave, onError)} processing={isSaving}>Save KDP</SydButton>
      </SydModalActions>
    </div>
  )
}

KDPDialogForm.propTypes = {
  kdp: PropTypes.object.isRequired,
  format: PropTypes.string.isRequired,
  close: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
  isSaving: PropTypes.bool.isRequired
}

export default KDPDialogForm
